import { Option } from "@inrev/core";
import {
	bureauLabel,
	type ExperianCreditDetail,
	FULL_REPORT_SECTIONS,
	failureCopy,
	formatReportDate,
	type IndividualCreditReport,
	type IndividualCreditReportFailure,
	vendorLabel,
} from "@portal/api/account/admin/individualCreditReport";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Banner, Button, Icon, Text } from "@portal/components/primitives";
import type { FC, ReactNode } from "react";
import { CreditSummarySection } from "./CreditSummarySection";
import { IdentitySection } from "./IdentitySection";
import { InquiriesSection } from "./InquiriesSection";
import { PublicRecordsSection } from "./PublicRecordsSection";
import { Fact } from "./ReportSection";
import { FactValue, ScoreHero, ScoreRangeBar, SuccessfulPullFacts } from "./ScoreHero";
import { TradelinesSection } from "./TradelinesSection";

type Report = IndividualCreditReport;
type ExperianReport = Extract<Report, { vendor: "experian" }>;
type SuccessOutcome<R extends Report> = Extract<R["outcome"], { status: "success" }>;

export type RepullControl = {
	/** Who the pull is for, named in the confirmation. */
	subject: string;
	run: () => void;
	pending: boolean;
	failed: boolean;
};

type ReportFooterProps = {
	onClose: () => void;
	repull: RepullControl;
	repullLabel: string;
};

const ReportFooter: FC<ReportFooterProps> = ({ onClose, repull, repullLabel }) => (
	<div className="mt-auto flex flex-col gap-4">
		{repull.failed && (
			<Banner tone="danger" data-testid="individual_credit_repull_error">
				<Banner.Leading>
					<Icon type="exclamation-triangle" color="danger" size="small" />
				</Banner.Leading>
				<Banner.Content>
					We couldn't start the pull. Try again; if it keeps failing, contact support.
				</Banner.Content>
			</Banner>
		)}
		<div className="flex justify-end gap-2">
			<Button
				color="secondary"
				variant="outline"
				onClick={onClose}
				data-testid="individual_credit_report_close"
			>
				Close
			</Button>
			<ConfirmationModalClickWrapper
				message={
					<div className="w-[375px] text-center">
						Pull a new credit report for {repull.subject}? This runs a fresh credit pull right away.
					</div>
				}
				confirmButtonText="Pull report"
				confirmationButtonColor="primary"
				onConfirm={repull.run}
			>
				<Button color="primary" loading={repull.pending} data-testid="individual_credit_repull">
					{repullLabel}
				</Button>
			</ConfirmationModalClickWrapper>
		</div>
	</div>
);

const UnavailableSections: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
	<div
		className="flex flex-col gap-3 rounded-xl border border-dashed border-bc-strong bg-surface-light px-5 py-4"
		data-testid="individual_credit_unavailable_sections"
	>
		<Text type="eyebrow" color="tertiary">
			{label}
		</Text>
		{children}
	</div>
);

const ScoreFactors: FC<{
	factors: ExperianCreditDetail["scoreFactors"];
}> = ({ factors }) =>
	factors.length === 0 ? null : (
		<div className="flex flex-col gap-2" data-testid="individual_credit_score_factors">
			<Text type="eyebrow" color="tertiary">
				Score Drivers{" "}
				<span className="font-normal normal-case tracking-normal">— in order of impact</span>
			</Text>
			<ol className="m-0 flex list-none flex-col gap-2 p-0">
				{factors.map((factor) => (
					<li key={factor.code} className="flex items-baseline gap-2.5">
						<Text
							type="caption"
							color="tertiary"
							className="shrink-0 rounded bg-surface-neutral px-1.5 py-0.5 tabular-nums"
						>
							{factor.code}
						</Text>
						<Text type="body" color="primary">
							{factor.description ?? `Reason code ${factor.code}`}
						</Text>
					</li>
				))}
			</ol>
		</div>
	);

export const ExperianReportBody: FC<{
	report: ExperianReport;
	outcome: SuccessOutcome<ExperianReport>;
}> = ({ report, outcome }) => (
	<div className="flex flex-col gap-4" data-testid="individual_credit_report_experian">
		<section className="flex flex-col gap-4 border-b border-bc-default pb-5">
			<ScoreHero
				bureau="Experian"
				score={outcome.score}
				model={outcome.model}
				aside={Option.isSome(outcome.score) && <ScoreRangeBar score={outcome.score} />}
				facts={
					<SuccessfulPullFacts
						bankruptcy={outcome.bankruptcy}
						pulledAt={report.createdAt}
						reusedUntil={outcome.reusedUntil}
					/>
				}
			/>
			{Option.isSome(outcome.detail) && <ScoreFactors factors={outcome.detail.scoreFactors} />}
		</section>
		{Option.isSome(outcome.detail) ? (
			<>
				<PublicRecordsSection detail={outcome.detail} />
				<CreditSummarySection detail={outcome.detail} />
				<TradelinesSection detail={outcome.detail} pulledAt={report.createdAt} />
				<InquiriesSection detail={outcome.detail} pulledAt={report.createdAt} />
				<IdentitySection detail={outcome.detail} />
			</>
		) : (
			<Banner tone="danger" data-testid="individual_credit_detail_unavailable">
				<Banner.Leading>
					<Icon type="exclamation-triangle" color="danger" size="small" />
				</Banner.Leading>
				<Banner.Content>
					We couldn't read the full Experian file for this pull, so only the score and bankruptcy
					flag are shown. Contact support if you need the detail.
				</Banner.Content>
			</Banner>
		)}
		<div
			className="flex items-center gap-2 border-t border-bc-default pt-4"
			data-testid="individual_credit_report_footer"
		>
			<Icon type="clock" size="small" color="tertiary" />
			<Text type="body" color="secondary">
				Pulled {formatReportDate(report.createdAt)} from Experian
			</Text>
		</div>
	</div>
);

type FailedReportBodyProps = {
	report: Report;
	failure: IndividualCreditReportFailure;
	onClose: () => void;
	repull: RepullControl;
};

export const FailedReportBody: FC<FailedReportBodyProps> = ({
	report,
	failure,
	onClose,
	repull,
}) => {
	const copy = failureCopy(failure, report.vendor);
	const frozen = failure.reason === "frozen";
	return (
		<div className="flex flex-col gap-4" data-testid="individual_credit_report_failed">
			<ScoreHero
				bureau={bureauLabel(report.vendor)}
				score={undefined}
				model={undefined}
				facts={
					<div className="grid grid-cols-3 gap-4">
						<Fact label="Attempted">
							<FactValue>
								<Text type="title" color="primary">
									{formatReportDate(report.createdAt)}
								</Text>
							</FactValue>
						</Fact>
						<Fact label="Failure Reason" testId="individual_credit_failure_reason">
							<FactValue>
								<Icon
									type={frozen ? "snowflake" : "x-circle"}
									size="small"
									color={frozen ? "accent" : "danger"}
								/>
								<Text type="title" color={frozen ? "accent" : "danger"}>
									{copy.label}
								</Text>
							</FactValue>
						</Fact>
					</div>
				}
			/>
			<Banner tone={copy.tone} data-testid="individual_credit_failure_banner">
				<Banner.Leading>
					<Icon
						type={copy.tone === "info" ? "information-circle" : "exclamation-triangle"}
						color={copy.tone === "info" ? "accent" : "danger"}
						size="small"
					/>
				</Banner.Leading>
				<Banner.Content>{copy.message}</Banner.Content>
			</Banner>
			<UnavailableSections label="Not available until the report is pulled">
				<Text type="body" color="secondary">
					{FULL_REPORT_SECTIONS.join(" · ")}
				</Text>
			</UnavailableSections>
			<ReportFooter
				onClose={onClose}
				repull={repull}
				repullLabel={frozen ? "Pull report again" : "Try again"}
			/>
		</div>
	);
};

type MeridianReport = Extract<Report, { vendor: "meridian" }>;

export const LegacyReportBody: FC<{
	report: MeridianReport;
	outcome: SuccessOutcome<MeridianReport>;
	onClose: () => void;
	repull: RepullControl;
}> = ({ report, outcome, onClose, repull }) => (
	<div className="flex flex-col gap-4" data-testid="individual_credit_report_legacy">
		<ScoreHero
			bureau="Equifax"
			score={outcome.score}
			model={outcome.model}
			facts={
				<SuccessfulPullFacts
					bankruptcy={outcome.bankruptcy}
					pulledAt={report.createdAt}
					reusedUntil={outcome.reusedUntil}
				/>
			}
		/>
		<Banner tone="neutral" data-testid="individual_credit_legacy_banner">
			<Banner.Leading>
				<Icon type="information-circle" color="secondary" size="small" />
			</Banner.Leading>
			<Banner.Content>
				Pulled from {vendorLabel(report.vendor)} before the switch to Experian. It has only the
				score and bankruptcy flag, and Equifax scores can differ from Experian's for the same
				person. Pull an Experian report for the full detail.
			</Banner.Content>
		</Banner>
		<ReportFooter onClose={onClose} repull={repull} repullLabel="Pull Experian report" />
	</div>
);

export const NoReportBody: FC<{ onClose: () => void; repull: RepullControl }> = ({
	onClose,
	repull,
}) => (
	<div className="flex flex-col gap-4" data-testid="individual_credit_report_none">
		<Text type="body" color="secondary">
			No credit report has been pulled for this individual yet. A report is pulled when the account
			is submitted for underwriting, or you can pull one now.
		</Text>
		<ReportFooter onClose={onClose} repull={repull} repullLabel="Pull report" />
	</div>
);
